import { logWarn } from './security/logger.js';
import { ACTION_PAGE_VIEW, TABLE_NAVIGATION } from './audit-navigation.js';
// ═══ AUREUS SOCIAL PRO — Audit Trail Serveur (RGPD Art. 30) ═══
// Toutes les actions sensibles tracées côté serveur via /api/audit
//
// ⚠️ TOUTE écriture du journal passe par `auditLog` ci-dessous — donc par `authFetch`.
// `sbFromRequest` ne lit QUE l'en-tête `Authorization`, et la route POST répond
// `200 { skipped: true }` quand il manque : un `fetch` nu ne produit aucune erreur
// et aucune ligne. Mesuré le 08/10/2026 : 0 PAGE_VIEW sur 18 865 lignes d'`audit_log`,
// trois `fetch` nus en cause. Verrou : __tests__/audit-aucun-fetch-nu.test.js.

var _currentUser = null;

export function setAuditUser(user) {
  _currentUser = user;
}

/**
 * Tracer une action côté serveur
 * @param {string} action - Ex: 'CREATE_EMPLOYEE', 'UPDATE_PAYSLIP', 'DELETE_CONTRACT'
 * @param {string} table_name - Table concernée
 * @param {string|null} record_id - ID de l'enregistrement
 * @param {object|null} details - Détails supplémentaires (avant/après, champs modifiés)
 */
export async function auditLog(action, table_name = null, record_id = null, details = null) {
  try {
    // Récupérer le token JWT depuis le singleton Supabase
    const { authFetch } = await import('./auth-fetch');
    // Pendant une impersonation, `_currentUser` (posé par setAuditUser) EST
    // la cible : l'action s'écrit sous son identité, avec l'IP de l'admin.
    // On marque la ligne — sans quoi elle est indiscernable d'une vraie
    // connexion du client, et pollue « Qui est en ligne » (bug 16/09/2026).
    // La trace RGPD Art. 30 reste complète : rien n'est retiré, seul un champ
    // s'ajoute.
    const { getImpersonationState } = await import('./impersonation');
    const impersonation = getImpersonationState();
    const finalDetails = impersonation
      ? { ...(details || {}), impersonated_by: impersonation.admin_email || null }
      : details;
    await authFetch('/api/audit', {
      method: 'POST',
      body: JSON.stringify({
        action,
        table_name,
        record_id,
        details: finalDetails,
        user_id: _currentUser?.id || null,
        user_email: _currentUser?.email || null,
      })
    });
  } catch (e) {
    logWarn('Audit', `Échec trace: ${action}`, e);
  }
}

// ── Journal des modules consultés (PAGE_VIEW) ───────────────────────────────
// UNE ligne par CHANGEMENT de page, jamais par rendu : le seul appelant est
// `setPage` (un clic de menu, un lien, un événement de navigation), pas un effet
// de rendu. Le marqueur d'impersonation et l'absence d'erreur visible viennent de
// `auditLog` — rien n'est réécrit ici.
//
// Deux garde-fous, tous deux mesurés plutôt que supposés :
//
//  1. DOUBLON IMMÉDIAT. Le même module deux fois en moins de 2 s est un double
//     déclenchement (un chemin de navigation qui repasse par `setPage`), pas deux
//     consultations. Une consultation REVENUE plus tard (page A → B → retour → B)
//     reste journalisée : c'est pourquoi on compare à une FENÊTRE, pas au dernier
//     module à vie — le retour arrière (popstate, bouton « précédent ») change de
//     page sans passer par ici, un « dernier module » mémorisé deviendrait faux.
//
//  2. PLAFOND DE DÉBIT. Chaque ligne est une requête sur `/api/*`, que `proxy.js`
//     compte par IP : 60 par minute au global, puis ban de 5 min → 24 h → 7 jours →
//     définitif au 3e dépassement. Une navigation frénétique (la démonstration à un
//     prospect, un test d'écran) y ajouterait autant de requêtes qu'il y a de pages.
//     Au-delà de 12 par minute — une page toutes les 5 s en continu, c'est du
//     balayage, plus de la consultation — on n'écrit plus, et la ligne SUIVANTE dit
//     combien ont été omises (`details.omises`) : le journal ne ment pas par silence.
export const PLAFOND_VUES_PAR_MINUTE = 12;
export const FENETRE_DOUBLON_MS = 2000;
const FENETRE_PLAFOND_MS = 60 * 1000;
var _dernierModule = null;   // dernier module navigué (écrit OU omis par le plafond)
var _dernierInstant = 0;     // quand
var _envois = [];            // horodatages (ms) des écritures de la dernière minute
var _omises = 0;             // vues non écrites à cause du plafond, annoncées sur la suivante

function _idPage(v) {
  return typeof v === 'string' ? v.trim().slice(0, 80) : '';
}

/**
 * @param {string} module     id de la page ouverte (ex. 'employees')
 * @param {string|null} precedent  id de la page quittée, telle que l'appelant la connaît
 * @param {number} [maintenant]    injectable pour les tests
 * @returns {Promise<void>} ne rejette JAMAIS — fire-and-forget, aucune erreur visible
 */
export function journaliserPageVue(module, precedent = null, maintenant = Date.now()) {
  try {
    const id = _idPage(module);
    if (!id) return Promise.resolve();

    if (id === _dernierModule && maintenant - _dernierInstant < FENETRE_DOUBLON_MS) {
      return Promise.resolve();
    }
    _dernierModule = id;
    _dernierInstant = maintenant;

    _envois = _envois.filter((t) => maintenant - t < FENETRE_PLAFOND_MS);
    if (_envois.length >= PLAFOND_VUES_PAR_MINUTE) {
      _omises += 1;
      return Promise.resolve();
    }
    _envois.push(maintenant);

    const omises = _omises;
    _omises = 0;
    const previous = _idPage(precedent) || null;
    return Promise.resolve(
      auditLog(ACTION_PAGE_VIEW, TABLE_NAVIGATION, id, {
        module: id,
        previous,
        ...(omises ? { omises } : {}),
      }),
    ).catch(() => { /* fire-and-forget */ });
  } catch {
    return Promise.resolve();
  }
}

// Helpers prêts à l'emploi
export const audit = {
  // Employés
  createEmployee: (emp) => auditLog('CREATE_EMPLOYEE', 'employees', emp?.id, { name: `${emp?.prenom} ${emp?.nom}`, niss_masked: emp?.niss ? emp.niss.substring(0,6)+'***' : null }),
  updateEmployee: (emp, fields) => auditLog('UPDATE_EMPLOYEE', 'employees', emp?.id, { name: `${emp?.prenom} ${emp?.nom}`, fields_changed: fields }),
  deleteEmployee: (emp) => auditLog('DELETE_EMPLOYEE', 'employees', emp?.id, { name: `${emp?.prenom} ${emp?.nom}` }),

  // Fiches de paie
  generatePayslip: (emp, month, year) => auditLog('GENERATE_PAYSLIP', 'fiches_paie', null, { employee: `${emp?.prenom} ${emp?.nom}`, period: `${month}/${year}` }),
  exportPayslip: (emp, month, year) => auditLog('EXPORT_PAYSLIP', 'fiches_paie', null, { employee: `${emp?.prenom} ${emp?.nom}`, period: `${month}/${year}` }),

  // Déclarations
  submitDimona: (emp, type) => auditLog('SUBMIT_DIMONA', 'dimona', null, { employee: `${emp?.prenom} ${emp?.nom}`, type }),
  submitDmfa: (quarter, year) => auditLog('SUBMIT_DMFA', 'dmfa', null, { quarter, year }),

  // Documents
  generateContract: (emp, type) => auditLog('GENERATE_CONTRACT', 'documents', null, { employee: `${emp?.prenom} ${emp?.nom}`, type }),

  // Auth
  login: (email) => auditLog('USER_LOGIN', 'auth', null, { email }),
  logout: (email) => auditLog('USER_LOGOUT', 'auth', null, { email }),

  // Backup
  backup: (records) => auditLog('BACKUP_GENERATED', 'backup', null, { total_records: records }),

  // Navigation (journal « Module consulté », écran Activité admin)
  pageView: (module, precedent) => journaliserPageVue(module, precedent),

  // Admin
  updateSettings: (section) => auditLog('UPDATE_SETTINGS', 'app_state', null, { section }),

  // Générique
  custom: (action, table, id, details) => auditLog(action, table, id, details),
};
